import React from 'react';
import { cn } from '@/utils';
import { ITableColumn, TableProps } from './Table.types';
import { TableHeader } from './TableHeader';
import { TableCell } from './TableCell';
import { TableSkeleton } from './TableSkeleton';
import { TextContent } from '@/components/Text';
import { Button } from '@/components/Buttons';
import { APP_STYLE_CONFIG } from '@/constants';

/**
 * Table Component
 *
 * A flexible and reusable table component that supports:
 * - Sortable columns
 * - Custom cell rendering
 * - Loading states
 * - Responsive design
 * - Custom styling
 * - Optional animation for row rendering
 *
 * @example
 * ```tsx
 * const columns = [
 *   {
 *     key: 'name',
 *     title: 'Name',
 *     sortable: true,
 *     render: (name, record) => <UserCell name={name} email={record.email} />
 *   },
 *   {
 *     key: 'status',
 *     title: 'Status',
 *     render: (status) => <StatusBadge status={status} />
 *     enabled: false, // This column will be hidden
 *   }
 * ];
 *
 * <Table
 *   title="Team Members"
 *   columns={columns}
 *   data={users}
 *   onSort={(key, direction) => handleSort(key, direction)}
 * />
 * ```
 */
export const Table = <T,>({
  columns,
  data,
  title,
  loading = false,
  bordered = true,
  rowKey = (record: T) => (record as any)?.id || Math.random(),
  onSort,
  onFilterClear,
  sortConfig,
  showFilterResultsText = false,
  filterResultsText,
  emptyContent,
  className,
  animateRows = APP_STYLE_CONFIG.useAnimatedTables,
  ...props
}: TableProps<T>) => {
  /**
   * Handle sorting when a sortable column header is clicked. Toggles between ascending,
   * descending, and no sort states.
   */
  const handleSort = (columnKey: string) => {
    if (!onSort) return;

    const newDirection =
      sortConfig?.key === columnKey && sortConfig.direction === 'asc' ? 'desc' : 'asc';

    onSort(columnKey, newDirection);
  };

  /**
   * Render the content of a cell. If the column definition includes a custom render function,
   * it will be used to render the cell content. Otherwise, it will display the raw value
   * from the data record.
   */
  const renderCell = (column: ITableColumn<T>, record: T, index: number) => {
    const value = (record as any)[column.key];

    if (column.render) {
      return column.render(value, record, index);
    }

    return value;
  };

  /**
   * Get the unique key for a table row. If the `rowKey` prop is a function, it will be called
   * with the record to get the key. Otherwise, it will default to using the `id` property of
   * the record or the index as a fallback.
   */
  const getRowKey = (record: T, index: number): string | number => {
    if (typeof rowKey === 'function') {
      return rowKey(record);
    }

    return (record as any)?.id || index;
  };

  return (
    <div className={cn('overflow-hidden rounded-lg bg-neutral-white', className)} {...props}>
      {/* Table Title */}
      {(title || showFilterResultsText) && (
        <div className="flex items-center justify-start gap-2.5 px-6 py-6">
          {title && <TextContent label={title} type="headline" variant="sectionTitle" level={2} />}

          {/* Filter Results */}
          {showFilterResultsText && (
            <div className="ms-auto flex animate-fade-in items-center justify-start gap-4 animate-duration-fast">
              {filterResultsText && (
                <TextContent
                  color="neutral-gray-700"
                  label={filterResultsText}
                  type="paragraph"
                  variant="bodyMedium"
                />
              )}
              <Button
                className="text-brand-primary hover:text-neutral-black"
                iconLeft="XCircle"
                label="Clear filter"
                onClick={onFilterClear}
                size="small"
                variant="link"
              />
            </div>
          )}
        </div>
      )}

      {/* Table Container with Horizontal Scroll */}
      <div className="px-0 pb-0">
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            {/* Table Header */}
            <thead>
              <tr className={cn('border-neutral-gray-300', { 'border-b border-t': bordered })}>
                {columns
                  .filter((column) => column.enabled !== false)
                  .map((column) => (
                    <TableHeader
                      key={column.key}
                      column={column}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                  ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {loading ? (
                <TableSkeleton columns={columns} />
              ) : data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.filter((column) => column.enabled !== false).length}
                    className="px-4 py-12"
                  >
                    {emptyContent || (
                      <p className="italic text-neutral-gray-700">No data available</p>
                    )}
                  </td>
                </tr>
              ) : (
                data.map((record, index) => (
                  <tr
                    key={getRowKey(record, index)}
                    style={animateRows ? { animationDelay: `${index * 50}ms` } : undefined}
                    className={cn(
                      animateRows && 'animate-fade-in animate-duration-faster',
                      bordered && 'border-b border-neutral-gray-300 last:border-b-0',
                    )}
                  >
                    {columns
                      .filter((column) => column.enabled !== false)
                      .map((column) => (
                        <TableCell
                          key={column.key}
                          column={column}
                          value={renderCell(column, record, index)}
                        />
                      ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

Table.displayName = 'Table';

export type { ITableColumn, TableProps };
export default Table;
